'use client';

// module imports
import html2canvas from 'html2canvas';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// file imports
import { DownloadAllResponses, ReportFeedbackCard, ReportImageComponent, StateMessage } from '@/components';
import { getFeedbackReports, updateFeedback } from '@/redux/slices/feedbackSlice';
import type { AppDispatch, RootState } from '@/redux/store';
import { IReport } from '@/types';

const MetricCard = ({ title, icon, value, subtitle, children, iconBgColor = 'bg-blue-100' }: { title: string; icon: React.ReactNode; value?: string | number; subtitle?: string; children?: React.ReactNode; iconBgColor?: string }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 lg:p-6 shadow-sm">
    <div className="flex items-center justify-between mb-3 sm:mb-4">
      <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800">{title}</h3>
      <div className={`w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 ${iconBgColor} rounded-lg flex items-center justify-center`}>{icon}</div>
    </div>
    {children || (
      <div className="flex items-center space-x-2 sm:space-x-3">
        <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        </div>
        <div>
          <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800">{value}</div>
          {subtitle && <div className="text-xs sm:text-sm text-gray-500">{subtitle}</div>}
        </div>
      </div>
    )}
  </div>
);

const ProgressBar = ({ label, percentage, color }: { label: string; percentage: string; color: string }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs sm:text-sm font-medium text-gray-700 w-16 sm:w-20 lg:w-24">{label}</span>
    <div className="flex-1 mx-2 sm:mx-3 lg:mx-4">
      <div className="relative h-2 sm:h-3 bg-gray-200 rounded-full overflow-hidden">
        <div className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500`} style={{ width: percentage, backgroundColor: color }}></div>
      </div>
    </div>
    <span className="text-xs sm:text-sm font-semibold text-gray-800 w-10 sm:w-12 text-right">{percentage}</span>
  </div>
);

const ReportBanner = ({ title, subtitle, icon }: { title: string; subtitle: string; icon: React.ReactNode }) => (
  <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 text-white">
    <div className="flex items-center space-x-3 sm:space-x-4 lg:space-x-6">
      <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-blue-500 rounded-full flex items-center justify-center">{icon}</div>
      <div>
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">{title}</h2>
        <p className="text-blue-100 text-sm sm:text-base lg:text-lg">{subtitle}</p>
      </div>
    </div>
  </div>
);

const ShareButton = ({ slug, reportType }: { slug: string; reportType: 'Event' | 'Presentation' }) => {
  const [isCopied, setIsCopied] = useState(false);

  const generateReportLink = () => {
    const baseUrl = window.location.origin;
    const reportPath = reportType === 'Event' ? `/events/${slug}/report` : `/presentations/${slug}/report`;
    return `${baseUrl}${reportPath}`;
  };

  const handleShareClick = async () => {
    try {
      const reportLink = generateReportLink();
      await navigator.clipboard.writeText(reportLink);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000); // Reset after 3 seconds
    } catch (error) {
      console.error('Error copying report link:', error);
      // Fallback: show the link in a prompt for manual copying
      const reportLink = generateReportLink();
      prompt('Copy this link to share the report:', reportLink);
    }
  };

  return (
    <>
      <button onClick={handleShareClick} className="flex items-center space-x-1 sm:space-x-2 text-blue-600 hover:text-blue-700 transition-colors cursor-pointer text-xs sm:text-sm">
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
        </svg>
        <span className="hidden sm:inline">Share Report Link</span>
        <span className="sm:hidden">Share</span>
      </button>

      {/* Toast Notification */}
      {isCopied && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-3 sm:px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2 animate-in slide-in-from-right duration-300 text-xs sm:text-sm">
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Link copied to clipboard!</span>
        </div>
      )}
    </>
  );
};

const DownloadButton = ({ data, slug }: { data: IReport; slug: string }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const reportRef = useRef<HTMLDivElement>(null);

  const generateReportImage = async () => {
    try {
      if (!reportRef.current) return null;

      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        width: 800,
        height: 1200,
      });

      return canvas;
    } catch (error) {
      console.error('Error generating report image:', error);
      return null;
    }
  };

  const handleShowPreview = async () => {
    const canvas = await generateReportImage();
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      setPreviewImageUrl(dataUrl);
      setShowPreview(true);
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
    }
  };

  const handleDownloadFromPreview = () => {
    if (previewImageUrl) {
      const a = document.createElement('a');
      a.href = previewImageUrl;
      a.download = `event-report-${slug}-${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    // Restore background scrolling
    document.body.style.overflow = 'unset';
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl('');
    }
  };

  return (
    <>
      {/* Hidden Report Component for html2canvas */}
      <div className="fixed -top-[9999px] left-0 pointer-events-none" ref={reportRef}>
        <ReportImageComponent data={data} />
      </div>

      <button onClick={handleShowPreview} className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-xs sm:text-sm">
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="hidden sm:inline">Download Report</span>
        <span className="sm:hidden">Download</span>
      </button>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleClosePreview}>
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Report Preview</h3>
              <div className="flex gap-2">
                <button onClick={handleDownloadFromPreview} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download</span>
                </button>
                <button onClick={handleClosePreview} className="p-2 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4">
              <Image src={previewImageUrl} alt="Report Preview" width={800} height={600} className="max-w-full h-auto rounded-lg shadow-lg" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// const GHLPushButton = ({ data, slug }: { data: IReport; slug: string }) => {
//   const [showGHLModal, setShowGHLModal] = useState(false);
//   const [isPushingToGHL, setIsPushingToGHL] = useState(false);
//   const [ghlMessage, setGhlMessage] = useState('');
//   const [locationId, setLocationId] = useState('');

//   const generateCSV = () => {
//     const csvData = [];

//     // Add summary data
//     csvData.push(['Report Summary']);
//     csvData.push([]);
//     csvData.push(['Metric', 'Value']);
//     csvData.push(['Total Responses', data.totalResponses || 0]);
//     csvData.push(['Valuable Percentage', data.valuableBreakdown?.percentage || '0%']);
//     csvData.push(['Would Hear Again Percentage', data.hearBreakdown?.percentage || '0%']);
//     csvData.push([]);

//     // Add valuable breakdown
//     if (data.valuableBreakdown) {
//       csvData.push(['Valuable Breakdown']);
//       csvData.push(['Metric', 'Value']);
//       csvData.push(['Valuable', data.valuableBreakdown.valuable?.count || 0]);
//       csvData.push(['Not Valuable', data.valuableBreakdown.notValuable?.count || 0]);
//       csvData.push([]);
//     }

//     // Add hear again breakdown
//     if (data.hearBreakdown) {
//       csvData.push(['Would Hear Again Breakdown']);
//       csvData.push(['Metric', 'Value']);
//       csvData.push(['Definitely Yes', data.hearBreakdown.definitelyYes?.count || 0]);
//       csvData.push(['Probably Yes', data.hearBreakdown.probablyYes?.count || 0]);
//       csvData.push(['Probably No', data.hearBreakdown.probablyNo?.count || 0]);
//       csvData.push(['Definitely Not', data.hearBreakdown.definitelyNot?.count || 0]);
//       csvData.push([]);
//     }

//     // Add session info
//     if (data.sessionInfo) {
//       csvData.push(['Session Information']);
//       csvData.push(['Metric', 'Value']);
//       csvData.push(['Interactive', data.sessionInfo.interactive?.percentage || '0%']);
//       csvData.push(['Actionable', data.sessionInfo.actionable?.percentage || '0%']);
//       csvData.push(['Inspiring', data.sessionInfo.inspiring?.percentage || '0%']);
//       csvData.push(['Relevant', data.sessionInfo.relevant?.percentage || '0%']);
//       csvData.push(['Engaging', data.sessionInfo.engaging?.percentage || '0%']);
//       csvData.push([]);
//     }

//     // Add user feedback
//     if (data.userInfo && data.userInfo.length > 0) {
//       csvData.push(['User Feedback']);
//       csvData.push(['Name', 'Email', 'Comments']);
//       data.userInfo.forEach((user) => {
//         csvData.push([user.name || 'Anonymous', user.email || '', (user.comments || 'No comment provided').replace(/\n/g, ' ').replace(/,/g, ';')]);
//       });
//       csvData.push([]);
//     }

//     // Add suggestions
//     if (data.suggestions && data.suggestions.length > 0) {
//       csvData.push(['Additional Feedback']);
//       csvData.push(['Suggestions']);
//       data.suggestions.forEach((suggestion) => {
//         csvData.push([suggestion.replace(/\n/g, ' ').replace(/,/g, ';')]);
//       });
//     }

//     return csvData.map((row) => row.join(',')).join('\n');
//   };

//   const handlePushToGHL = async () => {
//     if (!locationId.trim()) {
//       setGhlMessage('Please enter a valid Location ID');
//       return;
//     }

//     setIsPushingToGHL(true);
//     setGhlMessage('');

//     try {
//       const csvContent = generateCSV();

//       const response = await fetch('/api/ghl/push', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({
//           csvData: csvContent,
//           reportData: data,
//           slug: slug,
//           locationId: locationId.trim(),
//         }),
//       });

//       const result = await response.json();

//       if (response.ok) {
//         setGhlMessage('Successfully pushed to GoHighLevel!');
//         setLocationId('');
//         setTimeout(() => {
//           setShowGHLModal(false);
//           setGhlMessage('');
//         }, 2000);
//       } else {
//         setGhlMessage(result.error || 'Error pushing to GoHighLevel');
//       }
//     } catch (error) {
//       console.error('GHL push error:', error);
//       setGhlMessage('Error pushing to GoHighLevel. Please try again.');
//     } finally {
//       setIsPushingToGHL(false);
//     }
//   };

//   const handleCloseGHLModal = () => {
//     setShowGHLModal(false);
//     setGhlMessage('');
//     setLocationId('');
//   };

//   return (
//     <>
//       {/* <button onClick={() => setShowGHLModal(true)} className="ml-2 flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer"> */}
//       <button onClick={() => setShowGHLModal(true)} disabled className="ml-2 flex items-center space-x-2 px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed opacity-50">
//         <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
//         </svg>
//         <span>Push to GHL</span>
//       </button>

//       {/* GHL Push Modal */}
//       {showGHLModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCloseGHLModal}>
//           <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-lg font-semibold text-gray-800">Push to GoHighLevel</h3>
//               <button onClick={handleCloseGHLModal} className="p-2 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">
//                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                 </svg>
//               </button>
//             </div>

//             <div className="space-y-4">
//               <div>
//                 <label htmlFor="locationId" className="block text-sm font-medium text-gray-700 mb-2">
//                   Location ID
//                 </label>
//                 <input type="text" id="locationId" value={locationId} onChange={(e) => setLocationId(e.target.value)} placeholder="Enter your GoHighLevel Location ID" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" disabled={isPushingToGHL} />
//               </div>

//               <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
//                 <div className="flex items-start space-x-3">
//                   <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
//                   </svg>
//                   <div>
//                     <h4 className="text-sm font-medium text-blue-800 mb-1">Push CSV Data to GoHighLevel</h4>
//                     <p className="text-sm text-blue-700">This will push the feedback data directly to your GoHighLevel account using the Location ID above.</p>
//                   </div>
//                 </div>
//               </div>

//               {ghlMessage && <div className={`p-3 rounded-lg text-sm ${ghlMessage.includes('Successfully') ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>{ghlMessage}</div>}

//               <div className="flex gap-3 pt-4">
//                 <button onClick={handleCloseGHLModal} className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer" disabled={isPushingToGHL}>
//                   Cancel
//                 </button>
//                 <button onClick={handlePushToGHL} disabled={isPushingToGHL || !locationId.trim()} className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
//                   {isPushingToGHL ? (
//                     <>
//                       <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
//                       </svg>
//                       <span>Pushing...</span>
//                     </>
//                   ) : (
//                     <span>Push to GHL</span>
//                   )}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

const DownloadCSVButton = ({ data, slug }: { data: IReport; slug: string }) => {
  const generateCSV = () => {
    const csvData = [];

    // Add summary data
    csvData.push(['Report Summary']);
    csvData.push([]);

    // Add session info
    if (data.sessionInfo) {
      csvData.push(['Session Information']);
      csvData.push(['Metric', 'Value']);
      csvData.push(['Interactive', data.sessionInfo.interactive?.percentage || '0%']);
      csvData.push(['Actionable', data.sessionInfo.actionable?.percentage || '0%']);
      csvData.push(['Inspiring', data.sessionInfo.inspiring?.percentage || '0%']);
      csvData.push(['Relevant', data.sessionInfo.relevant?.percentage || '0%']);
      csvData.push(['Engaging', data.sessionInfo.engaging?.percentage || '0%']);
      csvData.push([]);
    }

    // Add user feedback
    if (data.userInfo && data.userInfo.length > 0) {
      csvData.push(['User Feedback']);
      csvData.push(['Name', 'Email', 'Comments']);
      data.userInfo.forEach((user) => {
        csvData.push([user.name || 'Anonymous', user.email || '', (user.comments || 'No comment provided').replace(/\n/g, ' ').replace(/,/g, ';')]);
      });
      csvData.push([]);
    }

    // Add suggestions
    if (data.suggestions && data.suggestions.length > 0) {
      csvData.push(['Additional Feedback']);
      csvData.push(['Suggestions']);
      data.suggestions.forEach((suggestion) => {
        csvData.push([suggestion.replace(/\n/g, ' ').replace(/,/g, ';')]);
      });
      csvData.push([]);
    }

    // Add trial metrics
    if (data.sessionInsights) {
      csvData.push(['Metrics']);
      csvData.push(['Metric', 'Value']);

      if (data.sessionInsights.sessionValue) {
        csvData.push(['Session Value Count', data.sessionInsights.sessionValue.count || 0]);
        csvData.push(['Session Value Percentage', data.sessionInsights.sessionValue.percentage || '0%']);
      }

      if (data.sessionInsights.immediateApplication) {
        csvData.push(['Immediate Application Count', data.sessionInsights.immediateApplication.count || 0]);
        csvData.push(['Immediate Application Percentage', data.sessionInsights.immediateApplication.percentage || '0%']);
      }

      if (data.sessionInsights.engagementLevel) {
        csvData.push(['Engagement Level Count', data.sessionInsights.engagementLevel.count || 0]);
        csvData.push(['Engagement Level Percentage', data.sessionInsights.engagementLevel.percentage || '0%']);
      }

      if (data.sessionInsights.topicRelevance) {
        csvData.push(['Topic Relevance Count', data.sessionInsights.topicRelevance.count || 0]);
        csvData.push(['Topic Relevance Percentage', data.sessionInsights.topicRelevance.percentage || '0%']);
      }

      if (data.sessionInsights.npsScore) {
        csvData.push(['NPS Average Score', data.sessionInsights.npsScore.average?.toFixed(1) || '0.0']);
        csvData.push(['NPS Response Count', data.sessionInsights.npsScore.count || 0]);
      }

      csvData.push([]);

      // Add memorable insights
      if (data.sessionInsights.memorableInsights && data.sessionInsights.memorableInsights.length > 0) {
        csvData.push(['Memorable Insights']);
        data.sessionInsights.memorableInsights.forEach((insight) => {
          csvData.push([insight.replace(/\n/g, ' ').replace(/,/g, ';')]);
        });
        csvData.push([]);
      }

      // Add speaker styles
      if (data.sessionInsights.speakerStyles && data.sessionInsights.speakerStyles.length > 0) {
        csvData.push(['Speaker Style Descriptions']);
        data.sessionInsights.speakerStyles.forEach((style) => {
          csvData.push([style.replace(/\n/g, ' ').replace(/,/g, ';')]);
        });
      }
    }

    return csvData.map((row) => row.join(',')).join('\n');
  };

  const handleDownloadCSV = () => {
    try {
      const csvContent = generateCSV();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `event-report-${slug}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };

  return (
    <>
      <div className="relative">
        <button onClick={handleDownloadCSV} className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer text-xs sm:text-sm">
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="hidden sm:inline">Download CSV</span>
          <span className="sm:hidden">CSV</span>
        </button>
      </div>
    </>
  );
};

export default function ReportPage({ slug, reportType }: { slug: string; reportType: 'Event' | 'Presentation' }) {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading, error } = useSelector((state: RootState) => state.feedback);

  // Type assertion to help TypeScript understand the data structure
  const reportData = data as IReport | null;

  useEffect(() => {
    if (slug && slug.trim()) {
      dispatch(getFeedbackReports(slug));
    }
  }, [dispatch, slug]);

  const handleStatusChange = async (submissionId: string, status: 'approved' | 'rejected') => {
    try {
      await dispatch(updateFeedback({ slug, feedbackId: submissionId, status })).unwrap();
      dispatch(getFeedbackReports(slug));
    } catch (error) {
      console.error('Error updating feedback status:', error);
    }
  };

  if (loading) {
    return <StateMessage type="loading" loadingMessage="Loading report..." notFoundMessage="Report not found" />;
  }

  if (error) {
    return <StateMessage type="error" loadingMessage="Loading report..." errorMessage={`Error: ${error}`} notFoundMessage="Report not found" />;
  }

  if (!reportData) {
    return <StateMessage type="not-found" loadingMessage="Loading report..." notFoundMessage="Report not found" />;
  }

  // At this point, reportData is guaranteed to be non-null
  const report = reportData!;
  const reportTitle = reportType === 'Event' ? 'Event Report' : 'Presentation Report';

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-start mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">{reportTitle}</h1>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
        <ShareButton slug={slug} reportType={reportType} />
        {/* <GHLPushButton data={reportData} slug={slug} /> */}
        <DownloadAllResponses slug={slug} />
        <DownloadCSVButton data={reportData} slug={slug} />
        <DownloadButton data={reportData} slug={slug} />
      </div>

      {/* Speaking Engagement Report Banner */}
      <ReportBanner
        title="Speaking Engagement Report"
        subtitle="Audience Feedback Report"
        icon={
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      />

      {/* Metrics Section */}
      {report.sessionInsights && (
        <div className="mt-6 sm:mt-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 lg:p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Session Value */}
              <MetricCard
                title="Session Value"
                icon={
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
                  </svg>
                }
                iconBgColor="bg-green-100"
                value={report.sessionInsights.sessionValue?.count || 0}
                subtitle={`${report.sessionInsights.sessionValue?.percentage || '0%'} found valuable`}
              />

              {/* Immediate Application */}
              <MetricCard
                title="Immediate Application"
                icon={
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                }
                iconBgColor="bg-blue-100"
                value={report.sessionInsights.immediateApplication?.count || 0}
                subtitle={`${report.sessionInsights.immediateApplication?.percentage || '0%'} will apply`}
              />

              {/* Engagement Level */}
              <MetricCard
                title="Engagement Level"
                icon={
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                }
                iconBgColor="bg-orange-100"
                value={report.sessionInsights.engagementLevel?.count || 0}
                subtitle={`${report.sessionInsights.engagementLevel?.percentage || '0%'} highly engaged`}
              />

              {/* Topic Relevance */}
              <MetricCard
                title="Topic Relevance"
                icon={
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                }
                iconBgColor="bg-purple-100"
                value={report.sessionInsights.topicRelevance?.count || 0}
                subtitle={`${report.sessionInsights.topicRelevance?.percentage || '0%'} found relevant`}
              />
            </div>

            {/* NPS Score */}
            {report.sessionInsights.npsScore && (
              <div className="mt-6 sm:mt-8">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-800">Net Promoter Score (NPS)</h4>
                    <div className="text-3xl font-bold text-blue-600">{report.sessionInsights.npsScore.average?.toFixed(1) || '0.0'}</div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Not likely (0)</span>
                        <span>Very likely (10)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div className="bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 h-3 rounded-full transition-all duration-500" style={{ width: `${(report.sessionInsights.npsScore.average || 0) * 10}%` }}></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Based on</div>
                      <div className="text-lg font-semibold text-gray-800">{report.sessionInsights.npsScore.count || 0} responses</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Horizontal Bar Chart Section */}
      {report.sessionInfo && (
        <div className="mt-6 sm:mt-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 lg:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800">Attendees found this session</h3>
              <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <ProgressBar label={report.sessionInfo.interactive?.label || 'Interactive'} percentage={report.sessionInfo.interactive?.percentage || '0%'} color="#ef4444" />
              <ProgressBar label={report.sessionInfo.actionable?.label || 'Actionable'} percentage={report.sessionInfo.actionable?.percentage || '0%'} color="#22c55e" />
              <ProgressBar label={report.sessionInfo.inspiring?.label || 'Inspiring'} percentage={report.sessionInfo.inspiring?.percentage || '0%'} color="#f97316" />
              <ProgressBar label={report.sessionInfo.relevant?.label || 'Relevant'} percentage={report.sessionInfo.relevant?.percentage || '0%'} color="#3b82f6" />
              <ProgressBar label={report.sessionInfo.engaging?.label || 'Engaging'} percentage={report.sessionInfo.engaging?.percentage || '0%'} color="#a855f7" />
            </div>
          </div>
        </div>
      )}

      {/* Report Results */}
      {report.userInfo && report.userInfo.length > 0 && (
        <div className="mt-6 sm:mt-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 lg:p-6 shadow-sm">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 sm:mb-6">Report Results</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              {report.userInfo.map((info, index: number) => (
                <ReportFeedbackCard
                  key={info.submissionId}
                  comment={info.comments || 'No comment provided'}
                  authorName={info.name || 'Attendee'}
                  authorEmail={info.email}
                  submissionId={info.submissionId}
                  slug={slug}
                  isHighlighted={index === 0} // Highlight first reportCard
                  status={info?.status || 'pending'}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Suggestions Section */}
      {report.suggestions && report.suggestions.length > 0 && (
        <div className="mt-6 sm:mt-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 lg:p-6 shadow-sm">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Additional Feedback</h3>
            <div className="space-y-2 sm:space-y-3">
              {report.suggestions.map((suggestion: string, index: number) => (
                <div key={index} className="p-2 sm:p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm sm:text-base text-gray-700">{suggestion}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Insights Section */}
      {report.sessionInsights && ((report.sessionInsights.memorableInsights && report.sessionInsights.memorableInsights.length > 0) || (report.sessionInsights.speakerStyles && report.sessionInsights.speakerStyles.length > 0)) && (
        <div className="mt-6 sm:mt-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 lg:p-6 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {/* Memorable Insights */}
              {report.sessionInsights.memorableInsights && report.sessionInsights.memorableInsights.length > 0 && (
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 sm:mb-4 flex items-center">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Memorable Insights
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    {report.sessionInsights.memorableInsights.map((insight: string, index: number) => (
                      <div key={index} className="p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm sm:text-base text-gray-700 italic">&ldquo;{insight}&rdquo;</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Speaker Styles */}
              {report.sessionInsights.speakerStyles && report.sessionInsights.speakerStyles.length > 0 && (
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 sm:mb-4 flex items-center">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Speaker Style Descriptions
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    {report.sessionInsights.speakerStyles.map((style: string, index: number) => (
                      <div key={index} className="p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-sm sm:text-base text-gray-700 italic">&ldquo;{style}&rdquo;</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
